import React, { useEffect, useRef, useState } from "react";
import { api } from "./api";

// 서버 시각(UTC, 타임존 표기 없음)을 한국 시각으로 표시
export function formatMemoWhen(iso) {
  if (!iso) return "";
  const d = new Date(/[Zz]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ko-KR", { timeZone: "Asia/Seoul", dateStyle: "medium", timeStyle: "short" });
}

/**
 * 프로젝트 메모장 — 제목 있는 메모장을 여러 개 만들고, 닫으면 최근 수정 순 카드로 보인다.
 * initialOpenId: 처음 열어 둘 메모장 id (기본정보 탭에서 제목을 눌러 들어온 경우)
 */
export default function ProjectMemos({ projectId, initialOpenId, onChange }) {
  const [memos, setMemos] = useState(null);
  const [error, setError] = useState("");
  // 편집 중인 메모장. id 가 없으면 아직 저장 전인 새 메모장.
  const [editing, setEditing] = useState(null);

  async function load() {
    setError("");
    try {
      const list = await api.get(`/projects/${projectId}/memos`);
      setMemos(list);
      return list;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }

  useEffect(() => {
    (async () => {
      const list = await load();
      if (list && initialOpenId) {
        const m = list.find((x) => x.id === initialOpenId);
        if (m) setEditing(m);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function handleClosed(changed) {
    setEditing(null);
    if (changed) {
      await load();
      if (onChange) onChange();
    }
  }

  if (editing) {
    return <MemoEditor projectId={projectId} memo={editing} onClose={handleClosed} />;
  }

  return (
    <div className="sstoa-section" style={{ marginTop: 16 }}>
      <div className="sstoa-section__head">
        <h2>메모장{memos && memos.length > 0 && <span className="sstoa-memos__cnt">{memos.length}</span>}</h2>
        <button
          type="button"
          className="sstoa-btn sstoa-btn--primary sstoa-btn--sm"
          onClick={() => setEditing({ title: "", body: "" })}
        >
          + 메모장 만들기
        </button>
      </div>
      {error && <div className="sstoa-error" style={{ marginBottom: 10 }}>{error}</div>}
      {memos === null && !error && <div className="sstoa-empty">불러오는 중…</div>}
      {memos && memos.length === 0 && (
        <div className="sstoa-panel sstoa-empty">아직 메모장이 없습니다. ‘메모장 만들기’로 새 메모장을 시작하세요.</div>
      )}
      {memos && memos.length > 0 && (
        <div className="sstoa-memos">
          {memos.map((m) => (
            <button key={m.id} type="button" className="sstoa-memos__card" onClick={() => setEditing(m)}>
              <div className="sstoa-memos__title">{m.title}</div>
              <div className="sstoa-memos__preview">{(m.body || "").trim() || "내용 없음"}</div>
              <div className="sstoa-memos__when">{formatMemoWhen(m.updated_at)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MemoEditor({ projectId, memo, onClose }) {
  const [title, setTitle] = useState(memo.title || "");
  const [body, setBody] = useState(memo.body || "");
  const [saved, setSaved] = useState(memo); // 마지막으로 서버에 저장된 상태
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const changedRef = useRef(false); // 이 편집에서 한 번이라도 저장/삭제했는지
  const titleRef = useRef(null);

  const isNew = !saved.id;
  const dirty = title !== (saved.title || "") || body !== (saved.body || "");

  useEffect(() => {
    if (isNew && titleRef.current) titleRef.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function save() {
    if (!title.trim()) {
      setError("메모장 제목을 입력하세요.");
      if (titleRef.current) titleRef.current.focus();
      return false;
    }
    setBusy(true); setError(""); setMsg("");
    try {
      const payload = { title: title.trim(), body };
      const res = saved.id
        ? await api.post(`/projects/${projectId}/memos/${saved.id}`, payload)
        : await api.post(`/projects/${projectId}/memos`, payload);
      setSaved(res);
      setTitle(res.title);
      changedRef.current = true;
      setMsg("저장되었습니다.");
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  // 닫기: 바뀐 내용이 있으면 저장하고 닫는다. 아무것도 안 쓴 새 메모장은 그냥 버린다.
  async function close() {
    if (busy) return;
    if (isNew && !title.trim() && !body.trim()) {
      onClose(changedRef.current);
      return;
    }
    if (dirty && !(await save())) return;
    onClose(changedRef.current);
  }

  async function remove() {
    if (busy || !saved.id) return;
    if (!window.confirm(`‘${saved.title}’ 메모장을 삭제할까요?`)) return;
    setBusy(true); setError("");
    try {
      await api.del(`/projects/${projectId}/memos/${saved.id}`);
      onClose(true);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <div className="sstoa-section" style={{ marginTop: 16 }}>
      <div className="sstoa-section__head">
        <h2>{isNew ? "새 메모장" : "메모장"}</h2>
        <div style={{ display: "flex", gap: 8 }}>
          {!isNew && (
            <button type="button" className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={remove} disabled={busy}>삭제</button>
          )}
          <button type="button" className="sstoa-btn sstoa-btn--primary sstoa-btn--sm" onClick={save} disabled={busy || (!dirty && !isNew)}>
            {busy ? "저장 중…" : "저장"}
          </button>
          <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={close} disabled={busy}>닫기</button>
        </div>
      </div>
      {error && <div className="sstoa-error" style={{ marginBottom: 10 }}>{error}</div>}
      {msg && !dirty && <div className="sstoa-error" style={{ marginBottom: 10, background: "#e7f6ec", borderColor: "#b6e0c4", color: "#1f7a3d" }}>{msg}</div>}
      <div className="sstoa-panel sstoa-panel--pad" style={{ display: "grid", gap: 10 }}>
        <input
          ref={titleRef}
          className="sstoa-input sstoa-memos__title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="메모장 제목"
          maxLength={200}
          disabled={busy}
        />
        <textarea
          className="sstoa-textarea"
          style={{ minHeight: 360 }}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="프로젝트 진행 메모를 자유롭게 입력하세요."
          disabled={busy}
        />
        {!isNew && (
          <div className="sstoa-memos__when">
            만든 날 {formatMemoWhen(saved.created_at)} · 마지막 수정 {formatMemoWhen(saved.updated_at)}
          </div>
        )}
      </div>
    </div>
  );
}
